import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useNewCampaignSchedule from 'c-main/Components/Schedule/useNewCampaignSchedule';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Alert, Checkbox, Chip, DialogV2 } from 'c-components';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import type { ScheduleGet } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { useAPIClientRequest } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import { NetworkRequestState } from 'c-data-layer';
import DetachDialogPop from 'c-main/Components/Schedule/Components/LineItems/PopTab/DetachDialogPop';

const PopTab = ({ id, handleOpenFilteredTab }) => {
    const {
        popDisplaysList,
        popDisplaysSelected,
        setPopDisplaysSelected,
        setExpandedScheduleId,
        setScheduleSearch,
        attachStateRequest,
        detachStateRequest,
        getPopDisplaysList,
    } = useNewCampaignSchedule(id);
    const [detach, setDetach] = useState({ schedules: [], screenIds: [] });
    const {
        start: detachStart,
        isLoading: detachLoading,
        reset: detachReset,
        error: detachError,
        requestState: detachRequestState,
    } = useAPIClientRequest(apiClient.Entities.Campaign.detachSchedules);

    useEffect(() => {
        if (detachRequestState === NetworkRequestState.Success) {
            setDetach({ schedules: [], screenIds: [] });
            detachReset();
            getPopDisplaysList();
            setPopDisplaysSelected([]);
        }
        if (attachStateRequest === NetworkRequestState.Success) {
            getPopDisplaysList();
            setPopDisplaysSelected([]);
        }
        if (detachStateRequest === NetworkRequestState.Success) {
            getPopDisplaysList();
            setPopDisplaysSelected([]);
        }
    }, [
        detachReset,
        detachRequestState,
        getPopDisplaysList,
        attachStateRequest,
        detachStateRequest,
        setPopDisplaysSelected,
    ]);

    const handleDetach = useCallback((screenIds, selectedSchedules) => {
        setDetach({
            screenIds,
            schedules: selectedSchedules.map(s => ({ id: s.id, name: s.name })),
        });
    }, []);

    const detachScheduleCall = useCallback(async () => {
        const scheduleIds = detach.schedules.map(schedule => schedule.id);
        const data = {
            schedules: scheduleIds,
            displays: detach.screenIds,
            line_items: [],
        };
        await detachStart(id, data);
    }, [id, detach.schedules, detach.screenIds, detachStart]);

    const toggleSelection = useCallback(
        row => {
            setPopDisplaysSelected(prev => {
                const isSelected = prev.some(popDisplay => popDisplay.id === row.id);
                return isSelected
                    ? prev.filter(popDisplay => popDisplay.id !== row.id)
                    : [...prev, row];
            });
        },
        [setPopDisplaysSelected],
    );

    const allSelected = useMemo(() => {
        if (popDisplaysList.length === 0) return false;
        return popDisplaysList.every(row =>
            popDisplaysSelected.some(popDisplay => popDisplay.id === row.id),
        );
    }, [popDisplaysSelected, popDisplaysList]);

    const toggleSelectAll = useCallback(() => {
        const currentlyPopDisplayedIds = popDisplaysList.map(row => row.id);

        setPopDisplaysSelected(prev => {
            const isAllSelected = currentlyPopDisplayedIds.every(id =>
                prev.some(popDisplay => popDisplay.id === id),
            );

            return isAllSelected
                ? prev.filter(popDisplay => !currentlyPopDisplayedIds.includes(popDisplay.id))
                : [...prev, ...popDisplaysList.filter(row => !prev.some(p => p.id === row.id))];
        });
    }, [popDisplaysList, setPopDisplaysSelected]);
    const t = useCommonTranslation();

    const columns = useMemo(
        () => [
            {
                field: 'select',
                headerName: '',
                width: 50,
                sortable: false,
                renderHeader: () => (
                    <Checkbox
                        checked={allSelected}
                        indeterminate={
                            popDisplaysSelected.length > 0 &&
                            popDisplaysList.some(
                                row =>
                                    !popDisplaysSelected.some(
                                        popDisplay => popDisplay.id === row.id,
                                    ),
                            )
                        }
                        onChange={toggleSelectAll}
                    />
                ),
                renderCell: ({ row }) => (
                    <Checkbox
                        checked={popDisplaysSelected.some(popDisplay => popDisplay.id === row.id)}
                        onChange={() => toggleSelection(row)}
                    />
                ),
            },
            {
                field: 'name',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.displaysTable.name'),
                flex: 2,
                renderCell: ({ row }) => (
                    <Box pt={2} pb={1}>
                        <Stack gap={1}>
                            <span>{row.name}</span>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                    handleOpenFilteredTab(
                                        {
                                            owner: row.owner ? [row.owner.id] : [],
                                            size: [row.size],
                                            environment: [row.env],
                                            type: [row.type],
                                        },
                                        row.name,
                                        row.screens_count,
                                    )
                                }
                            >
                                {row.screens_count}{' '}
                                {t('Modules.Main.Campaigns.CampaignSchedule.displaysTable.screens')}
                            </Button>
                        </Stack>
                    </Box>
                ),
            },
            {
                field: 'owner',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.table.ownerCol'),
                renderCell: ({ row }) => row?.owner?.name ?? '',
                flex: 1,
            },
            {
                field: 'size',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.displaysTable.size'),
                flex: 1,
            },
            {
                field: 'env',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.displaysTable.env'),
                flex: 1,
            },
            {
                field: 'schedules',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.displaysTable.schedule'),
                minWidth: 550,
                sortable: false,
                renderCell: ({ row }) => {
                    const schedules = row.schedules;
                    return (
                        <Box>
                            {row.schedules.map((schedule: ScheduleGet) => {
                                if (schedule.is_file) {
                                    return (
                                        <Chip
                                            key={schedule.id}
                                            onClick={() => [
                                                setExpandedScheduleId(schedule.id),
                                                setScheduleSearch(schedule.name),
                                            ]}
                                            label={schedule.name}
                                        />
                                    );
                                }
                                if (schedule.name === 'Default') {
                                    return (
                                        <Chip
                                            key={schedule.id}
                                            onClick={() => [
                                                setExpandedScheduleId(schedule.id),
                                                setScheduleSearch(schedule.name),
                                            ]}
                                            label={schedule.name}
                                        />
                                    );
                                }
                                return (
                                    <Chip
                                        key={schedule.id}
                                        onDelete={() => [handleDetach(row.screen_ids, [schedule])]}
                                        onClick={() => [
                                            setExpandedScheduleId(schedule.id),
                                            setScheduleSearch(schedule.name),
                                        ]}
                                        label={schedule.name}
                                    />
                                );
                            })}
                            <Button
                                variant="text"
                                onClick={() => handleDetach(row.screen_ids, schedules)}
                            >
                                {t('Modules.Main.Campaigns.CampaignSchedule.table.clearAll')}
                            </Button>
                        </Box>
                    );
                },
            },
        ],
        [
            t,
            allSelected,
            popDisplaysSelected,
            popDisplaysList,
            toggleSelectAll,
            toggleSelection,
            handleOpenFilteredTab,
            setExpandedScheduleId,
            setScheduleSearch,
            handleDetach,
        ],
    );
    return (
        <>
            <DataGridPro columns={columns} rows={popDisplaysList} getRowHeight={() => 'auto'} />
            {Array.isArray(detach?.screenIds) && detach.screenIds.length > 0 && (
                <DialogV2
                    open
                    onClose={() => setDetach({ schedules: [], screenIds: [] })}
                    title={t('Modules.Main.Campaigns.CampaignSchedule.detachDialogTitle')}
                    maxWidth="md"
                >
                    <Box display="flex" flexDirection="column" height="100%">
                        <Box flex={1} overflow="auto">
                            <DetachDialogPop detach={detach} setDetach={setDetach} />
                        </Box>
                        <Box
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'background.paper',
                                padding: 2,
                                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={detachScheduleCall}
                                disabled={detachRequestState === NetworkRequestState.InProgress}
                            >
                                {detachLoading ? (
                                    <CircularProgress />
                                ) : (
                                    t('Modules.Main.Campaigns.CampaignSchedule.detachDialogButton')
                                )}
                            </Button>
                        </Box>
                        {detachError && (
                            <Box mt={2}>
                                <Alert severity="error">{String(detachError)}</Alert>
                            </Box>
                        )}
                    </Box>
                </DialogV2>
            )}
        </>
    );
};

export default PopTab;
